import axios from "axios";
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Nouveau mot de passe — MAXREVIX";
  }, []);
  const token = new URLSearchParams(location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password !== confirm) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, { token, password }, { withCredentials: true });
      setDone(true);
      toast.success("Mot de passe réinitialisé ! Connectez-vous avec votre nouveau mot de passe.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail), { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-night text-white">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="glass fixed inset-x-0 top-0 z-50 border-b border-line">
        <div className="mx-auto flex h-[72px] w-full max-w-7xl items-center justify-between px-5 lg:px-8">
          <Link data-testid="reset-logo-link" to="/"><Logo /></Link>
          <Link
            data-testid="reset-back-home-btn"
            to="/"
            className="flex items-center gap-2 rounded-full border border-line px-4 py-2.5 text-[13px] font-bold text-white transition-colors duration-200 hover:border-neon hover:text-neon"
          >
            <ArrowLeft size={15} className="text-neon" />
            Accueil
          </Link>
        </div>
      </header>

      <main className="relative mx-auto w-full max-w-md px-5 pb-24 pt-32 lg:px-8">
        {done ? (
          <div className="mt-10 rounded-3xl border border-green-400/30 bg-gradient-to-b from-green-400/10 via-ink to-ink p-10 text-center">
            <p className="font-display text-3xl font-extrabold uppercase tracking-tight text-white">Mot de passe modifié !</p>
            <p className="mt-3 text-sm text-slate-400">Connectez-vous maintenant avec votre nouveau mot de passe.</p>
            <button
              data-testid="reset-go-login-btn"
              onClick={() => navigate("/profil", { replace: true })}
              className="mt-8 w-full rounded-full bg-neon px-6 py-3.5 text-xs font-extrabold uppercase tracking-wider text-night chip-glow"
            >
              Se connecter
            </button>
          </div>
        ) : (
          <div className="mt-10 rounded-3xl border border-neon/25 bg-gradient-to-b from-neon/10 via-ink to-ink p-8 lg:p-10">
            <h1 data-testid="reset-title" className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
              Nouveau mot de passe
            </h1>
            <p className="mt-3 text-sm text-slate-400">
              {token ? "Choisissez votre nouveau mot de passe (8 caractères minimum)." : "Lien de réinitialisation manquant ou incomplet."}
            </p>
            {token && (
              <form onSubmit={submit} className="mt-8 grid gap-4 text-left" data-testid="reset-form">
                <label className="grid gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Nouveau mot de passe</span>
                  <input data-testid="reset-input-password" required minLength={8} maxLength={72} type="password" autoComplete="new-password" className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} />
                </label>
                <label className="grid gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Confirmer</span>
                  <input data-testid="reset-input-confirm" required minLength={8} type="password" autoComplete="new-password" className="field-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </label>
                <button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={busy}
                  className="flex items-center justify-center gap-2 rounded-full bg-neon px-6 py-3.5 text-xs font-extrabold uppercase tracking-wider text-night chip-glow transition-transform duration-200 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-night/30 border-t-night" /> : null}
                  Réinitialiser mon mot de passe
                </button>
              </form>
            )}
          </div>
        )}
      </main>
    </div>
  );
}